/**
 * Single source of truth for the legal language that must travel with every
 * artefact the platform emits (PDF, GeoJSON, DXF, share links).
 *
 * This mirrors the disclaimer shown in the site footer and on /disclaimer.
 */
import { DISCLAIMER_VERSION, TERMS_VERSION } from "@/lib/legal-versions";

/* -------------------------------------------------- preliminary vocabulary */
/**
 * Short-form wording reused across every surface that shows a result. Keeping
 * these in one place is what stops the framing from drifting between the app,
 * the PDFs, the exported geometry and the emails.
 */

/** Badge / heading label. */
export const PRELIMINARY_LABEL = "Preliminary results";

/** One-sentence notice for cards, panels and email intros. */
export const PRELIMINARY_SHORT =
  "Preliminary results for due diligence only. Confirm with a licensed surveyor, title company, or attorney before acting.";

/** Compact stamp for captions, map exhibits and export file headers. */
export const PRELIMINARY_MICRO = "Preliminary. Not a survey or title opinion.";

/**
 * Title Policy Analyzer caution. Shown on screen, in the exported PDF and on
 * saved title analyzes so the AI reading of Schedule B is never mistaken for a
 * title opinion.
 */
export const TITLE_ANALYSIS_CAUTION =
  "AI-extracted Schedule B summary. Preliminary only. Have a title company or real estate attorney review the recorded documents before you rely on it.";

/** Title-block name used on every generated document. */
export const PRELIMINARY_DOC_TITLE = "Preliminary Easement & Boundary Review";

/** Footer/report disclaimer shown in the product UI. */
export const PLATFORM_DISCLAIMER =
  "Easements.com provides preliminary, technology-based estimates intended to aid your due diligence, and is not a law firm or licensed land surveyor. Boundary overlays, rights-of-way indicators, and AI analysis are preliminary, are for informational purposes, and may contain errors or omissions. Verify all legal encumbrances, title commitments, and boundary lines with a licensed professional before taking action. Use is subject to our Terms of Service.";

/** Extra language specific to exported geometry leaving the platform. */
export const GEOMETRY_DISCLAIMER =
  "Drift-corrected geometry is preliminary and is derived from user-placed ground control points over aerial imagery. It is not a boundary survey, is not certified, and must not be used to set monuments, fences, or structures.";

/** Combined block embedded in the header of every exported file. */
export const EXPORT_DISCLAIMER = `${PRELIMINARY_MICRO} ${PLATFORM_DISCLAIMER} ${GEOMETRY_DISCLAIMER} Terms of Service v${TERMS_VERSION} · Professional Advice Disclaimer v${DISCLAIMER_VERSION}. Easements.com · support@easements.com · easements.com/disclaimer`;

/* ------------------------------------------------------------- site footer */
/**
 * The one disclaimer sentence rendered in the global site footer. Every page
 * gets it exactly once, from the SiteFooter component in the root layout — never
 * re-render it inside a page.
 */
export const FOOTER_DISCLAIMER =
  "For reference only: Easements.com is a due-diligence aid, not legal advice, a title opinion, or a boundary survey, and using it creates no attorney-client relationship. Confirm with a licensed professional in your state. Use of this site means you accept our Terms of Service and Privacy Policy.";

/**
 * Quiet one-line notice shown under the site header on every page, so the
 * reference-only framing is visible before any article, guide, or result.
 */
export const REFERENCE_ONLY_NOTICE =
  "General reference only. Not legal advice. Talk to a licensed attorney in your state about your situation.";

/** Legal / support links shown beside the footer disclaimer. */
export const LEGAL_LINKS = [
  { label: "About", to: "/about" },
  { label: "Support", to: "/contact/support" },
  { label: "Legal", to: "/contact/legal" },
  { label: "Terms", to: "/terms" },
  { label: "Privacy", to: "/privacy" },
  { label: "Legal disclaimer", to: "/disclaimer" },
  { label: "Admin", to: "/admin" },
] as const;

/* ------------------------------------------------------- tier-aware wording */
/** Plan tiers, mirrored from billing-plans to keep this module dependency-free. */
export type DisclaimerTier = "free" | "starter" | "pro" | "team";

/**
 * Extra sentence appended to the report disclaimer based on the account tier.
 * Free reports carry the stronger "sample / unverified sources" caution;
 * paid tiers get the professional-use wording. The core disclaimer never
 * changes — only this trailing sentence.
 */
export const TIER_DISCLAIMER_NOTE: Record<DisclaimerTier, string> = {
  free: "Free preliminary report. Generated from public data sources without human review. Verify every finding with a licensed professional before you rely on it.",
  starter:
    "Membership report. Prepared for professional due-diligence use. Findings remain preliminary and are not a certified survey or title opinion.",
  pro: "Membership report. Prepared for professional due-diligence use. Findings remain preliminary and are not a certified survey or title opinion.",
  team: "Team report. Prepared for professional due-diligence use and may be shared with clients. Findings remain preliminary and are not a certified survey or title opinion.",
};

/** Tier note, defaulting to the strictest (free) wording when tier is unknown. */
export function tierDisclaimerNote(tier: DisclaimerTier | undefined | null): string {
  return TIER_DISCLAIMER_NOTE[tier ?? "free"] ?? TIER_DISCLAIMER_NOTE.free;
}

/** Full on-screen/report disclaimer for a tier: shared base + tier sentence. */
export function reportDisclaimerFor(tier: DisclaimerTier | undefined | null): string {
  return `${PRELIMINARY_MICRO} ${PLATFORM_DISCLAIMER} ${tierDisclaimerNote(tier)}`;
}

/* ------------------------------------------------- knowledge hub reference */
/**
 * Short note shown beside general legal-reference content in the Knowledge Hub
 * (for example the state-by-state prescriptive easement table). Deliberately
 * worded so it does not repeat the site footer disclaimer sentence, which must
 * render exactly once per page.
 */
export const LEGAL_REFERENCE_NOTE =
  "General educational reference only. Prescription periods and their exceptions change, and several states set the period by case law rather than by statute. Read the linked official code and consult a licensed attorney in that state before relying on any of it.";
